import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getResumeLocation, clearResumeLocation, roleDefaultPath } from '../utils/sessionResume';

/**
 * After an inactivity auto-logout, the user's last location is stashed (see
 * InactivityTimeout + sessionResume). On their next login this modal offers to
 * take them straight back there. "Yes" navigates to the saved route; "No" sends
 * them to their role's default landing. Either choice clears the stashed entry.
 */
const ResumeSessionModal: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [resumePath, setResumePath] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) { setResumePath(null); return; }
    const path = getResumeLocation(user.id);
    if (path) setResumePath(path);
  }, [isAuthenticated, user?.id]);

  if (!isAuthenticated || !resumePath) return null;

  const handleResume = () => {
    const target = resumePath;
    clearResumeLocation();
    setResumePath(null);
    navigate(target);
  };

  const handleDecline = () => {
    clearResumeLocation();
    setResumePath(null);
    navigate(roleDefaultPath(user));
  };

  const label = decodeURIComponent(resumePath);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v5h5M3.05 13A9 9 0 106 5.3L3 8" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center mb-2">
          Welcome back
        </h2>

        {/* Message */}
        <p className="text-gray-600 dark:text-gray-300 text-center mb-3">
          You were signed out for inactivity. Want to pick up where you left off?
        </p>

        {/* Destination preview */}
        <div className="mb-6 text-center">
          <span className="inline-block max-w-full truncate px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-mono text-gray-600 dark:text-gray-300">
            {label}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleResume}
            className="w-full px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-blue-700 transition-all"
          >
            Take me back
          </button>
          <button
            onClick={handleDecline}
            className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
          >
            No, start fresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResumeSessionModal;
